import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Form } from 'react-bootstrap';
import { Option } from '@pmi/dsm-react/dist/components/menus/types';
import { InputField } from '@pmi/dsm-react';
import { useFormikContext } from 'formik';
import { useStatesQueryMutation } from '../apiSlice';

export interface BillingAddressDetails {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  state: string;
  stateLabel: string;
  postal: string;
}

export interface BillingAddressValidationErrors {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  country?: string;
  state?: string;
  postal?: string;
}

export interface BillingAddressProps {
  countries: { text: string; value: string }[];
  setBillingAddress: Dispatch<SetStateAction<BillingAddressDetails>>;
}

export default function BillingAddress({
  countries,
  setBillingAddress,
}: BillingAddressProps) {
  const formik = useFormikContext<BillingAddressDetails>();
  const [stateOptions, setStateOptions] = useState<Option[]>([]);
  const updateBillingAddress = (
    field: keyof BillingAddressDetails,
    value: string,
  ) => {
    formik.setFieldValue(field, value);
  };
  const [statesQuery, { isLoading: statesLoading }] = useStatesQueryMutation();

  const countryOptions: Option[] = countries.map((c) => ({
    label: c.text,
    value: c.value,
  }));

  const fetchStates = useCallback(
    async (country: string) =>
      statesQuery({
        country,
      }).unwrap(),
    [statesQuery],
  );

  const onCountryInputChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target as HTMLSelectElement;
    formik.setFieldTouched('country', true);
    formik.setFieldTouched('state', true);
    formik.setFieldValue('country', value);
    formik.setFieldValue('state', '');
    formik.setFieldValue('stateLabel', '');
    formik.setFieldValue('postal', '');

    fetchStates(value).then((response) => {
      setStateOptions(
        response?.map((opt: { text: string; value: string }) => ({
          label: opt.text,
          value: opt.value,
        })),
      );
    });
  };

  const onStateInputChanged = (e: ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target as HTMLSelectElement;
    const stateOption = stateOptions.find((option) => option.value === value);
    formik.setFieldTouched('state', true);
    formik.setFieldValue('state', value);
    formik.setFieldValue('stateLabel', stateOption?.label || '');
  };

  useEffect(() => {
    setBillingAddress(formik.values);
  }, [formik.values, setBillingAddress]);

  useEffect(() => {
    if (formik.values.country) {
      fetchStates(formik.values.country).then((response) => {
        setStateOptions(
          response?.map((opt: { text: string; value: string }) => ({
            label: opt.text,
            value: opt.value,
          })),
        );
      });
    }
  }, [fetchStates, formik.values.country]);

  return (
    <div className="d-block mb-1">
      <Form.Group>
        <div className="row mb-3">
          <div className="col-12">
            <InputField
              type="text"
              label="Address Line 1"
              placeholder=""
              value={formik.values.addressLine1}
              isInvalid={Boolean(formik.errors.addressLine1)}
              onChange={(e) =>
                updateBillingAddress('addressLine1', e.target.value)
              }
              required
            />
            {formik.errors.addressLine1 && (
              <div className="invalid-feedback d-block">
                {formik.errors.addressLine1}
              </div>
            )}
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-12">
            <InputField
              type="text"
              label="Address Line 2"
              placeholder=""
              value={formik.values.addressLine2}
              isInvalid={Boolean(formik.errors.addressLine2)}
              onChange={(e) =>
                updateBillingAddress('addressLine2', e.target.value)
              }
            />
            {formik.errors.addressLine2 && (
              <div className="invalid-feedback d-block">
                {formik.errors.addressLine2}
              </div>
            )}
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-6">
            <InputField
              type="text"
              label="City"
              placeholder=""
              value={formik.values.city}
              isInvalid={Boolean(formik.errors.city)}
              onChange={(e) => updateBillingAddress('city', e.target.value)}
              required
            />
            {formik.errors.city && (
              <div className="invalid-feedback d-block">
                {formik.errors.city}
              </div>
            )}
          </div>
          <div className="col-6">
            <div className="dsm-input-field__label">
              <label
                className="dsm-input-dropdown__form-label dsm-input-dropdown__form-label--required"
                htmlFor="countryRegion"
              >
                Country/Region
              </label>
            </div>
            <Form.Select
              id="countryRegion"
              placeholder="Select Country/Region"
              onChange={onCountryInputChange}
              disabled={statesLoading}
              required
              value={formik.values.country}
              className={`dsm-input-field__form-control form-select ${
                formik.errors.country
                  ? 'dsm-input-field__form-control--error'
                  : ''
              }`}
            >
              <option key="" value="" className="dsm-menu__label">
                Select Country/Region
              </option>
              {countryOptions?.map((option) => (
                <option
                  className="dsm-menu__label"
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {formik.errors.country && (
              <div className="invalid-feedback d-block">
                {formik.errors.country}
              </div>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="dsm-input-field__label">
              <label
                className="dsm-input-dropdown__form-label dsm-input-dropdown__form-label--required"
                htmlFor="countryRegion"
              >
                State/Province
              </label>
            </div>
            <Form.Select
              placeholder="Select State/Province"
              onChange={onStateInputChanged}
              required
              disabled={statesLoading}
              value={formik.values.state}
              className={`dsm-input-field__form-control form-select ${
                formik.errors.state
                  ? 'dsm-input-field__form-control--error'
                  : ''
              }`}
            >
              <option>Select State/Province</option>
              {stateOptions?.map((option) => (
                <option
                  className="dsm-menu__label"
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {formik.errors.state && (
              <div className="invalid-feedback d-block">
                {formik.errors.state}
              </div>
            )}
          </div>
          <div className="col-6">
            <InputField
              type="text"
              label="Postal"
              placeholder=""
              value={formik.values.postal}
              isInvalid={Boolean(formik.errors.postal)}
              onChange={(e) => updateBillingAddress('postal', e.target.value)}
              required
            />
            {formik.errors.postal && (
              <div className="invalid-feedback d-block">
                {formik.errors.postal}
              </div>
            )}
          </div>
        </div>
      </Form.Group>
    </div>
  );
}
